import React from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function parseMeta(meta) {
  if (!meta) return {};
  if (typeof meta === 'string') {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return meta;
}

function getPlanFeatures(metadata) {
  const parsed = parseMeta(metadata);

  if (Array.isArray(parsed?.features_list)) {
    return parsed.features_list.filter(Boolean);
  }

  if (parsed?.features && typeof parsed.features === 'object') {
    return Object.entries(parsed.features)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key.replace('feature_', '').replaceAll('_', ' '));
  }

  return [];
}

function isPopularPlan(metadata) {
  const parsed = parseMeta(metadata);
  return Boolean(parsed?.popular_plan || parsed?.most_popular || parsed?.popular);
}

function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="landing-section-heading text-center">
      {eyebrow ? <span className="landing-section-heading__eyebrow">{eyebrow}</span> : null}
      {title ? <h2 className="landing-section-heading__title">{title}</h2> : null}
      {subtitle ? <p className="landing-section-heading__subtitle">{subtitle}</p> : null}
    </div>
  );
}

function PlanCard({ plan, content }) {
  const features = getPlanFeatures(plan?.metadata);
  const isPopular = isPopularPlan(plan?.metadata);

  return (
    <Col xl={4} lg={6} md={6} className="d-flex">
      <Card className={`pricing-card-v2 w-100 border-0 ${isPopular ? 'pricing-card-v2--popular' : ''}`}>
        <Card.Body className="pricing-card-v2__body">
          {isPopular && content?.pricing_popular_label ? (
            <div className="pricing-card-v2__badge">{content.pricing_popular_label}</div>
          ) : null}

          {plan?.name ? <h3 className="pricing-card-v2__title">{plan.name}</h3> : null}

          <div className="pricing-card-v2__price-wrap">
            <div className="pricing-card-v2__price">
              <span className="pricing-card-v2__currency">$</span>
              <span className="pricing-card-v2__amount">
                {Number(plan?.monthly_price_usd || 0).toLocaleString()}
              </span>
            </div>

            {content?.pricing_monthly_suffix ? (
              <span className="pricing-card-v2__suffix">{content.pricing_monthly_suffix}</span>
            ) : null}
          </div>

          {content?.pricing_yearly_prefix ? (
            <p className="pricing-card-v2__annual">
              {content.pricing_yearly_prefix} ${Number(plan?.annual_price_usd || 0).toLocaleString()}
            </p>
          ) : null}

          {features.length ? (
            <ul className="pricing-card-v2__features">
              {features.map((feature, index) => (
                <li key={`${plan?.id || 'plan'}-${index}`}>{feature}</li>
              ))}
            </ul>
          ) : null}

          {content?.pricing_button_text ? (
            <Button
              as={Link}
              to={content?.pricing_button_url || '/signup'}
              className="pricing-card-v2__button"
            >
              {content.pricing_button_text}
            </Button>
          ) : null}
        </Card.Body>
      </Card>
    </Col>
  );
}

export default function LandingPricing({ plans, content }) {
  if (!plans.length) return null;

  return (
    <section className="landing-section pricing-section-v2">
      <Container>
        <SectionHeading
          eyebrow={content?.pricing_eyebrow || 'Pricing'}
          title={content?.pricing_title || 'Flexible plans for every growth stage'}
          subtitle={
            content?.pricing_subtitle ||
            'Choose the package that matches your team size, workflow needs, and expansion goals.'
          }
        />

        <Row className="g-4 justify-content-center">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} content={content} />
          ))}
        </Row>
      </Container>
    </section>
  );
}